import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Plus, Trash2, Check, ChevronsUpDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface Course {
  id: string;
  title: string;
  duration_hours: number;
}

interface CertificateTemplate {
  id: string;
  name: string;
}

interface CourseAssignment {
  id: string;
  course_id: string;
  certificate_template_id: string;
  is_active: boolean;
  course_title: string;
  template_name: string;
  course_hours: number;
}

export function CourseAssignments() {
  const [assignments, setAssignments] = useState<CourseAssignment[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [templates, setTemplates] = useState<CertificateTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [newAssignment, setNewAssignment] = useState({
    course_id: '',
    certificate_template_id: ''
  });
  const [courseOpen, setCourseOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load available courses
      const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .select('id, title, duration_hours')
        .eq('status', 'published');

      if (courseError) throw courseError;

      // Load available templates
      const { data: templateData, error: templateError } = await supabase
        .from('certificate_templates')
        .select('id, name')
        .eq('is_active', true);

      if (templateError) throw templateError;

      console.log('Loaded courses:', courseData);
      console.log('Loaded templates:', templateData);

      // Mock assignments data for now
      const mockAssignments: CourseAssignment[] = [
        {
          id: '1',
          course_id: courseData?.[0]?.id || '1',
          certificate_template_id: templateData?.[0]?.id || '1',
          is_active: true,
          course_title: courseData?.[0]?.title || 'Sample Course',
          template_name: templateData?.[0]?.name || 'Default Template',
          course_hours: courseData?.[0]?.duration_hours || 10
        }
      ];

      setAssignments(mockAssignments);
      setCourses(courseData || []);
      setTemplates(templateData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load course assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAssignment = async () => {
    if (!newAssignment.course_id || !newAssignment.certificate_template_id) {
      toast.error('Please select both a course and certificate template');
      return;
    }

    try {
      const course = courses.find(c => c.id === newAssignment.course_id);
      const template = templates.find(t => t.id === newAssignment.certificate_template_id);

      if (!course || !template) {
        toast.error('Invalid course or template selection');
        return;
      }

      // Mock assignment creation
      const newAssignmentData: CourseAssignment = {
        id: Date.now().toString(),
        course_id: newAssignment.course_id,
        certificate_template_id: newAssignment.certificate_template_id,
        is_active: true,
        course_title: course.title,
        template_name: template.name,
        course_hours: course.duration_hours
      };

      setAssignments(prev => [...prev, newAssignmentData]);
      toast.success('Course certificate assignment created');
      setNewAssignment({ course_id: '', certificate_template_id: '' });
    } catch (error) {
      console.error('Error creating assignment:', error);
      toast.error('Failed to create assignment');
    }
  };

  const handleDeleteAssignment = async (id: string) => {
    if (!confirm('Are you sure you want to remove this assignment?')) return;

    try {
      setAssignments(prev => prev.filter(a => a.id !== id));
      toast.success('Assignment removed');
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error('Failed to remove assignment');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading assignments...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Course Certificate Assignments</h3>
        <p className="text-sm text-muted-foreground">
          Assign certificate templates to courses for automatic certificate generation
        </p>
      </div>

      {/* Create New Assignment */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Assign Certificate to Course</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Popover open={courseOpen} onOpenChange={setCourseOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={courseOpen}
                    className="w-full justify-between"
                  >
                    {newAssignment.course_id
                      ? courses.find((course) => course.id === newAssignment.course_id)?.title
                      : "Select course..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Search courses..." />
                    <CommandList>
                      <CommandEmpty>No courses found.</CommandEmpty>
                      <CommandGroup>
                        {courses.map((course) => (
                          <CommandItem
                            key={course.id}
                            value={course.title}
                            onSelect={() => {
                              setNewAssignment(prev => ({ ...prev, course_id: course.id }));
                              setCourseOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                newAssignment.course_id === course.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div>
                              <div className="font-medium">{course.title}</div>
                              <div className="text-sm text-muted-foreground">{course.duration_hours} hours</div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Popover open={templateOpen} onOpenChange={setTemplateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={templateOpen}
                    className="w-full justify-between"
                  >
                    {newAssignment.certificate_template_id
                      ? templates.find((template) => template.id === newAssignment.certificate_template_id)?.name
                      : "Select template..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Search templates..." />
                    <CommandList>
                      <CommandEmpty>No templates found.</CommandEmpty>
                      <CommandGroup>
                        {templates.map((template) => (
                          <CommandItem
                            key={template.id}
                            value={template.name}
                            onSelect={() => {
                              setNewAssignment(prev => ({ ...prev, certificate_template_id: template.id }));
                              setTemplateOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                newAssignment.certificate_template_id === template.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {template.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Button onClick={handleCreateAssignment} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Assign
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Assignments */}
      <div className="space-y-4">
        {assignments.map((assignment) => (
          <Card key={assignment.id}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <h4 className="font-medium">{assignment.course_title}</h4>
                    <p className="text-sm text-muted-foreground">
                      {assignment.course_hours} hours
                    </p>
                  </div>
                  <div className="text-muted-foreground">→</div>
                  <div>
                    <p className="font-medium">{assignment.template_name}</p>
                    <Badge variant={assignment.is_active ? 'default' : 'secondary'}>
                      {assignment.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => handleDeleteAssignment(assignment.id)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {assignments.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">
              No certificate assignments found. Create your first assignment above.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}